// pgw 05-may-2026 - testing ugc


import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "player-components";
import { selectUser } from "../../features/auth/authSlice";

import {
  getUgcLibrarySongs,
  saveUgcLibrarySongs,
  createUgcStemZip,
  uploadUgcZipToS3,
  getCurrentUgcUserEmail,
  buildUgcSong,
  fetchRemoteUgcLibrarySongs,
  uploadUgcSongLibraryJson,
} from "../../utils/ugcTesting";


const STEM_LABELS = ["BT", "Sopr", "Alto", "Tenor", "Bass"];

const detectStemLabelFromFilename = (filename) => {
  const name = String(filename || "").toLowerCase();

  if (/(^|[^a-z])bt([^a-z]|$)/i.test(filename) || name.includes("band") || name.includes("backing") || name.includes("accompaniment")) {
    return "BT";
  }

  if (name.includes("sopr")) return "Sopr";
  if (name.includes("alto")) return "Alto";
  if (name.includes("tenor")) return "Tenor";
  if (name.includes("bass")) return "Bass";

  return "";
};

const autoAssignStemFiles = (files) => {
  const assignments = {};

  files.forEach((file) => {
    const detectedLabel = detectStemLabelFromFilename(file.name);

    if (detectedLabel && !assignments[detectedLabel]) {
      assignments[detectedLabel] = file;
    }
  });

  return assignments;
};

export default function UgcLibraryEditor() {
  const currentUser = useSelector(selectUser);
  const [rows, setRows] = useState([]);

  useEffect(() => {
	const loadSongs = async () => {

	  const songs = await fetchRemoteUgcLibrarySongs();

	  console.log("[UGC] UgcLibraryEditor loaded fresh remote songlist =", songs.map((song) => ({
		songId: song.songId,
		mix: song.mix,
		chaptersCount: song.chapters?.length || 0,
		lyrics: song.lyrics,
		lyricsUrl: song.lyricsUrl,
	  })));

// 	  const songs = remoteSongs.length
// 		? remoteSongs
// 		: getUgcLibrarySongs();
// 


	  setRows(
		songs.map((song) => ({
		  songId: song.songId,
		  mix: song.mix || "",
		  len: song.len || 0,
		  pathzip: song.pathzip || "",
		  stemNames: Array.isArray(song.tracks)
			? song.tracks.map((track) => track.name)
			: [],

		  existingSong: song,
		  stemFiles: [],
		  stemAssignments: {},
		  uploading: false,

		  error: "",
		}))
	  );


	};

	loadSongs();
  }, []);


  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, ix) => (ix === index ? { ...row, ...patch } : row))
    );
  };


  const updateStemFiles = (rowIndex, files) => {
    const stemFiles = Array.from(files || []);
    const stemAssignments = autoAssignStemFiles(stemFiles);

    console.log("[UGC] selected stem files =", {
      rowIndex,
      files: stemFiles.map((file) => file.name),
      autoAssigned: Object.fromEntries(
        Object.entries(stemAssignments).map(([stem, file]) => [stem, file.name])
      ),
    });

    setRows((prev) =>
      prev.map((row, ix) => (
        ix === rowIndex
          ? {
              ...row,
              stemFiles,
              stemAssignments,
            }
          : row
      ))
    );
  };

  const updateStemAssignment = (rowIndex, fileIndex, stemLabel) => {
    setRows((prev) =>
      prev.map((row, ix) => {
        if (ix !== rowIndex) return row;

        const file = row.stemFiles?.[fileIndex];
        const nextAssignments = { ...(row.stemAssignments || {}) };

        Object.keys(nextAssignments).forEach((key) => {
          if (nextAssignments[key] === file) {
            delete nextAssignments[key];
          }
        });

        if (stemLabel) {
          nextAssignments[stemLabel] = file;
        }

        console.log("[UGC] manual stem assignment =", {
          rowIndex,
          filename: file?.name,
          stemLabel,
          assignments: Object.fromEntries(
            Object.entries(nextAssignments).map(([stem, assignedFile]) => [stem, assignedFile.name])
          ),
        });

        return {
          ...row,
          stemAssignments: nextAssignments,
        };
      })
    );
  };

  const getAssignedStemForFile = (row, file) => {
    const found = Object.entries(row.stemAssignments || {}).find(([, assignedFile]) => assignedFile === file);
    return found ? found[0] : "";
  };


  const addRow = () => {
    setRows((prev) => [
      ...prev,


	  {
		songId: `ugc-${Date.now()}`,
		mix: "",
		len: 0,
		pathzip: "",
		stemNames: [],
		existingSong: {},
		stemFiles: [],
		stemAssignments: {},
		uploading: false,
		error: "",
	  },


    ]);
  };

  const saveRowsToLibrary = async (nextRows = rows) => {
	const songs = nextRows
	  .filter((row) => row.mix && row.len && row.pathzip)
	  .map((row) =>


		buildUgcSong({
		  songId: row.songId,
		  mix: row.mix,
		  len: row.len,
		  pathzip: row.pathzip,
		  stemNames: row.stemNames,
		  existingSong: row.existingSong,
		})

	  );

	saveUgcLibrarySongs(songs);

	try {
	  await uploadUgcSongLibraryJson(songs);
	  console.log("UGC song library uploaded.");
	} catch (e) {
	  console.error("Could not upload UGC song library", e);
	  alert("Could not upload song library.");
	}
  };

  const uploadRow = async (index) => {
    const row = rows[index];

    if (!row.mix) {
      updateRow(index, { error: "Please enter a song title first." });
      return;
    }

    if (!row.len) {
      updateRow(index, { error: "Please enter song length in seconds." });
      return;
    }

	const uploadedStemCount = Object.values(row.stemAssignments || {}).filter(Boolean).length;

	if (uploadedStemCount < 1) {
	  updateRow(index, { error: "Please choose at least 1 stem file and assign it to a stem." });
	  return;
	}

    updateRow(index, { uploading: true, error: "" });

    try {
	  const { zipBlob, uploadedStemNames } = await createUgcStemZip(row.stemAssignments);
	  const filename = `${getCurrentUgcUserEmail()}/${row.songId}.zip`;

	  const publicUrl = await uploadUgcZipToS3(zipBlob, filename);

      const nextRows = rows.map((r, ix) =>
        ix === index


		  ? {
			  ...r,
			  pathzip: publicUrl,
			  stemNames: uploadedStemNames,
			  existingSong: {
				...(r.existingSong || {}),
				pathzip: publicUrl,
			  },
			  uploading: false,
			  error: "",
			}

          : r
      );

      setRows(nextRows);
	  await saveRowsToLibrary(nextRows);

      alert("Song uploaded and added to UGC library.");
    } catch (e) {
      console.error(e);
      updateRow(index, {
        uploading: false,
        error: e.message || "Upload failed",
      });
    }
  };

  if (!currentUser || currentUser.guest) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">User song library editor</h1>
        <p>You must be logged in to edit your song library.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">User song library editor</h1>


      <div className="overflow-auto border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1 text-left">len</th>
              <th className="border px-2 py-1 text-left">mix</th>

              <th className="border px-2 py-1 text-left">stems</th>

              <th className="border px-2 py-1 text-left">Upload</th>
              <th className="border px-2 py-1 text-left">S3 URL</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.songId}>
                <td className="border px-2 py-1">
                  <input
                    className="w-24 border px-1"
                    type="number"
                    value={row.len}
                    onChange={(e) =>
                      updateRow(rowIndex, { len: Number(e.target.value || 0) })
                    }
                    onBlur={() => saveRowsToLibrary()}
                  />
                </td>

                <td className="border px-2 py-1">
                  <input
                    className="w-64 border px-1"
                    value={row.mix}
                    onChange={(e) =>
                      updateRow(rowIndex, { mix: e.target.value })
                    }
                    onBlur={() => saveRowsToLibrary()}
                  />
                </td>



                <td className="border px-2 py-1">
                  <input
                    type="file"
                    accept="audio/*"
                    multiple
                    onChange={(e) => updateStemFiles(rowIndex, e.target.files)}
                  />

                  {row.stemFiles?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {row.stemFiles.map((file, fileIndex) => (
                        <div key={`${file.name}-${fileIndex}`} className="flex items-center gap-2">
                          <span className="w-64 truncate" title={file.name}>
                            {file.name}
                          </span>

                          <select
                            className="border px-1 py-0"
                            value={getAssignedStemForFile(row, file)}
                            onChange={(e) => updateStemAssignment(rowIndex, fileIndex, e.target.value)}
                          >
                            <option value="">Ignore</option>
                            {STEM_LABELS.map((label) => (
                              <option key={label} value={label}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}

                  {row.stemAssignments && Object.keys(row.stemAssignments).length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      Matched:{" "}
                      {Object.entries(row.stemAssignments)
                        .map(([stem, file]) => `${stem}=${file.name}`)
                        .join(", ")}
                    </div>
                  )}
                </td>


                <td className="border px-2 py-1">
                  <Button
                    disabled={row.uploading}
                    onClick={() => uploadRow(rowIndex)}
                  >
                    {row.uploading ? "Uploading..." : "Zip + upload"}
                  </Button>

                  {row.error && (
                    <div className="text-red-600 mt-1">{row.error}</div>
                  )}
                </td>

                <td className="border px-2 py-1 break-all">
                  {row.pathzip}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex gap-2">
        <Button onClick={addRow}>Add new empty row</Button>

        <Button onClick={async () => await saveRowsToLibrary()}>
          Save library
        </Button>
      </div>
    </div>
  );
}

